import { useState, useCallback, useEffect } from 'react';
import { Conversation, Message } from '../types/ai';
import { User, Tenant } from '../types/user';
import { Assistant, AssistantConversation } from '../types/assistant';
import { aiProviders } from '../data/ai-providers';
import { aiService } from '../services/ai-service';
import { conversationService } from '../services/supabase-service';
import { assistantService } from '../services/assistant-service';
import { plansService } from '../services/plans-service';
import { TopHeader } from './TopHeader';
import { NewAISidebar } from './NewAISidebar';
import { NewChatInterface } from './NewChatInterface';
import { DefaultChatScreen } from './DefaultChatScreen';
import { AssistantsList } from './AssistantsList';
import { AssistantEditorModal } from './AssistantEditorModal';
import { AssistantChatWrapper } from './AssistantChatWrapper';
import { AssistantTemplate } from '../types/assistant';
import { MobileInitialScreen } from './MobileInitialScreen';
import { MobileSidebarDrawer } from './MobileSidebarDrawer';
import { MobileChatLayout } from './MobileChatLayout';
import { UserManagementScreen } from './UserManagementScreen';
import PlansModal from './PlansModal';
import { useToast } from '../hooks/use-toast';
import { useMobile } from '../hooks/use-mobile';
import { processSpreadsheet, isSpreadsheet, processPDF, isPDF } from '../utils/file-processor';
import { supabase } from '../lib/supabase';
import { UserProfile } from './UserProfile';
import { OnboardingModal } from './OnboardingModal';
import { tenantService } from '../services/supabase-service';

interface UserDashboardProps {
  user: User;
  tenant: Tenant;
  onLogout: () => void;
  onCompanySettings?: () => void;
  onUserUpdate?: (user: User) => void;
}

export function UserDashboard({ user, tenant, onLogout, onCompanySettings, onUserUpdate }: UserDashboardProps) {
  const [activeProvider, setActiveProvider] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [selectedModels, setSelectedModels] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [executingTools, setExecutingTools] = useState<{ id: string; name: string; status: 'pending' | 'executing' | 'completed' | 'failed' }[]>([]);
  
  // Estado para triggerar refresh do contador de uso
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Estado para modal de upsell
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  
  // Estado para onboarding
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  
  // Estados para mobile
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [mobileView, setMobileView] = useState<'home' | 'chat'>('home');
  
  // Estado para sidebar em telas médias (tablet/desktop pequeno)
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  
  // Estados para agentes
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [activeAssistant, setActiveAssistant] = useState<Assistant | null>(null);
  const [assistantConversation, setAssistantConversation] = useState<AssistantConversation | null>(null);
  const [currentView, setCurrentView] = useState<'chat' | 'assistants' | 'assistant-chat' | 'user-management'>('chat');
  const [editingAssistant, setEditingAssistant] = useState<Assistant | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<AssistantTemplate | null>(null);
  const [isEditorModalOpen, setIsEditorModalOpen] = useState(false);
  
  
  const toast = useToast();
  const isMobile = useMobile();

  // Filtrar apenas IAs habilitadas para o tenant
  const availableProviders = aiProviders.filter(provider => 
    tenant.settings?.enabledAIs?.includes(provider.id) ?? true
  );

  // Verificar se precisa mostrar onboarding
  useEffect(() => {
    const checkOnboarding = async () => {
      try {
        // Verificar se o usuário já completou o onboarding
        const { data: authUser } = await supabase.auth.getUser();
        
        if (authUser?.user?.user_metadata?.onboarding_completed === false) {
          console.log('👋 Novo usuário detectado! Mostrando onboarding...');
          setShowOnboarding(true);
        } else {
          setOnboardingCompleted(true);
        }
      } catch (error) {
        console.error('Erro ao verificar onboarding:', error);
        setOnboardingCompleted(true); // Se der erro, assume que já foi feito
      }
    };

    checkOnboarding();
  }, []);

  // Carregar conversas do usuário ao montar o componente
  useEffect(() => {
    const loadConversations = async () => {
      try {
        console.log('📂 Carregando conversas do usuário:', user.id);
        
        // ⚠️ TEMPORARIAMENTE DESABILITADO: Limpeza de conversas vazias
        // (para debug - vamos ver se isso está causando o problema)
        /*
        try {
          const cleanedCount = await conversationService.cleanupEmptyConversations(user.id, tenant.id);
          if (cleanedCount > 0) {
            console.log(`🧹 ${cleanedCount} conversas vazias foram removidas automaticamente`);
          }
        } catch (cleanupError) {
          console.warn('Aviso: Não foi possível limpar conversas vazias:', cleanupError);
        }
        */

        const userConversations = await conversationService.getUserConversations(user.id, tenant.id);
        console.log(`📊 ${userConversations.length} conversas encontradas no banco`);
        
        // Filtrar conversas que têm mensagens (usando message_count do banco)
        const conversationsWithContent = userConversations.filter(conv => 
          conv.messageCount !== undefined && conv.messageCount > 0
        );
        
        console.log(`✅ ${conversationsWithContent.length} conversas com mensagens`);
        
        // Carregar mensagens apenas para as conversas que serão exibidas
        const conversationsWithMessages = await Promise.all(
          conversationsWithContent.map(async (conv) => {
            const messages = await conversationService.getConversationMessages(conv.id);
            return {
              ...conv,
              messages
            };
          })
        );
        
        console.log(`✅ Mensagens carregadas para ${conversationsWithMessages.length} conversas`);
        setConversations(conversationsWithMessages);
      } catch (error) {
        console.error('❌ Erro ao carregar conversas:', error);
        toast.error('Erro ao carregar conversas');
      }
    };

    // Só carregar se tiver user.id e tenant.id
    if (user?.id && tenant?.id) {
    loadConversations();
    }

    // 🔄 REALTIME: Escutar mudanças nas mensagens
    const channel = supabase
      .channel('messages-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `user_id=eq.${user.id}`
        },
        async (payload) => {
          console.log('🔄 Nova mensagem recebida via Realtime:', payload);
          
          const newMessage = payload.new as any;
          
          // Converter para formato Message
          const message: Message = {
            id: newMessage.id,
            content: newMessage.content,
            role: newMessage.role,
            timestamp: new Date(newMessage.created_at),
            modelId: newMessage.model_id,
            modelName: newMessage.model_name,
            attachments: newMessage.attachments || []
          };

          // Atualizar conversa com a nova mensagem
          setConversations(prev => 
            prev.map(conv => 
              conv.id === newMessage.conversation_id
                ? { ...conv, messages: [...conv.messages, message] }
                : conv
            )
          );
        }
      )
      .subscribe();

    // Cleanup ao desmontar
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user.id, tenant.id]);

  // Limpeza periódica de conversas vazias (a cada 5 minutos)
  useEffect(() => {
    const cleanupInterval = setInterval(async () => {
      try {
        const cleanedCount = await conversationService.cleanupEmptyConversations(user.id, tenant.id);
        if (cleanedCount > 0) {
          console.log(`🧹 Limpeza periódica: ${cleanedCount} conversas vazias removidas`);
          // Recarregar conversas se alguma foi removida
          const userConversations = await conversationService.getUserConversations(user.id, tenant.id);
          const conversationsWithMessages = await Promise.all(
            userConversations.map(async (conv) => {
              const messages = await conversationService.getConversationMessages(conv.id);
              return { ...conv, messages };
            })
          );
          const conversationsWithContent = conversationsWithMessages.filter(conv => 
            conv.messages && conv.messages.length > 0
          );
          setConversations(conversationsWithContent);
        }
      } catch (error) {
        console.warn('Aviso: Limpeza periódica falhou:', error);
      }
    }, 5 * 60 * 1000); // 5 minutos

    return () => clearInterval(cleanupInterval);
  }, [user.id, tenant.id]);

  // Carregar agentes do usuário
  useEffect(() => {
    const loadAssistants = async () => {
      try {
        const data = await assistantService.getAssistantsByUser(user.id, tenant.id);
        setAssistants(data);
      } catch (error) {
        console.error('Erro ao carregar agentes:', error);
      }
    };

    loadAssistants();
  }, [user.id, tenant.id]);

  // Mostrar notificação se há IAs desabilitadas (apenas para gestores)
  useEffect(() => {
    if (user.role === 'manager' && availableProviders.length < aiProviders.length) {
      // Mostrar apenas uma vez por sessão
      const hasShownNotification = sessionStorage.getItem('ai_disabled_notification_shown');
      if (!hasShownNotification) {
        toast.info(`${aiProviders.length - availableProviders.length} IA(s) estão desabilitadas nas configurações da empresa.`);
        sessionStorage.setItem('ai_disabled_notification_shown', 'true');
      }
    }
  }, [availableProviders.length, user.role]);

  // Função para criar uma nova conversa
  const createNewConversation = useCallback(async (providerId: string, modelId: string): Promise<Conversation> => {
    const provider = availableProviders.find(p => p.id === providerId);
    const model = provider?.models.find(m => m.id === modelId);
    
    const title = `Nova conversa com ${model?.name || provider?.name}`;
    
    try {
      const newConversation = await conversationService.createConversation(
        user.id,
        tenant.id,
        providerId,
        modelId,
        title
      );
      
      return {
        ...newConversation,
        messages: []
      };
    } catch (error) {
      console.error('Erro ao criar conversa:', error);
      toast.error('Erro ao criar nova conversa');
      throw error;
    }
  }, [availableProviders, user.id, tenant.id]);

  const handleProviderSelect = useCallback((providerId: string) => {
    setActiveProvider(providerId);
    setActiveConversationId(null); // Limpar conversa ativa ao trocar de provedor
    setActiveAssistant(null); // Limpar agente ativo
    setAssistantConversation(null); // Limpar conversa do agente
    setCurrentView('chat'); // Voltar para a view de chat
    
    // Se não há modelo selecionado para este provedor, selecione o primeiro
    if (!selectedModels[providerId]) {
      const provider = availableProviders.find(p => p.id === providerId);
      if (provider && provider.models.length > 0) {
        setSelectedModels(prev => ({
          ...prev,
          [providerId]: provider.models[0].id
        }));
      }
    }
    // NÃO criar conversa automaticamente - apenas quando houver mensagem
  }, [selectedModels, availableProviders]);

  const handleConversationSelect = useCallback((conversationId: string) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation) {
      setActiveConversationId(conversationId);
      setActiveProvider(conversation.aiId);
      setSelectedModels(prev => ({
        ...prev,
        [conversation.aiId]: conversation.modelId
      }));
      setActiveAssistant(null); // Limpar agente ativo
      setAssistantConversation(null); // Limpar conversa do agente
      setCurrentView('chat'); // Voltar para a view de chat
      
      // No mobile, ir para a tela de chat
      if (isMobile) {
        setMobileView('chat');
      }
    }
  }, [conversations, isMobile]);

  // Funções específicas para mobile
  const handleMobileProviderSelect = useCallback((providerId: string, modelId: string) => {
    setActiveProvider(providerId);
    setActiveConversationId(null);
    setSelectedModels(prev => ({
      ...prev,
      [providerId]: modelId
    }));
    setActiveAssistant(null);
    setAssistantConversation(null);
    setMobileView('chat');
  }, []);

  const handleMobileBackToHome = useCallback(() => {
    setMobileView('home');
    setActiveProvider(null);
    setActiveConversationId(null);
    setActiveAssistant(null);
    setAssistantConversation(null);
  }, []);

  const handleMobileNewChat = useCallback(() => {
    setActiveConversationId(null);
    setActiveAssistant(null);
    setAssistantConversation(null);
    setMobileView('home');
  }, []);

  const handleMobileConversationSelect = useCallback((conversation: Conversation) => {
    setActiveConversationId(conversation.id);
    setActiveProvider(conversation.aiId);
    setSelectedModels(prev => ({
      ...prev,
      [conversation.aiId]: conversation.modelId
    }));
    setActiveAssistant(null);
    setAssistantConversation(null);
    setMobileView('chat');
  }, []);

  const handleNewConversation = useCallback(async (providerId: string) => {
    const provider = availableProviders.find(p => p.id === providerId);
    if (!provider || !provider.models.length) return;
    
    const modelId = provider.models[0].id;
    
    try {
      const newConversation = await createNewConversation(providerId, modelId);
      setConversations(prev => [newConversation, ...prev]);
      setActiveConversationId(newConversation.id);
      setActiveProvider(providerId);
      setSelectedModels(prev => ({
        ...prev,
        [providerId]: modelId
      }));
    } catch (error) {
      console.error('Erro ao criar nova conversa:', error);
    }
  }, [createNewConversation, availableProviders]);

  const handleModelChange = useCallback((modelId: string) => {
    if (!activeProvider) return;
    
    setSelectedModels(prev => ({
      ...prev,
      [activeProvider]: modelId
    }));
    // NÃO criar conversa automaticamente - apenas quando houver mensagem
  }, [activeProvider]);

  const handleProviderChange = useCallback(async (newProviderId: string) => {
    if (!activeConversationId) return;
    
    const newProvider = availableProviders.find(p => p.id === newProviderId);
    if (!newProvider || !newProvider.models.length) return;
    
    const newModelId = newProvider.models[0].id;
    
    try {
      // Atualizar a conversa no Supabase com a nova IA
      await conversationService.updateConversationAI(
        activeConversationId,
        newProviderId,
        newModelId
      );
      
      // Atualizar o estado local
      setConversations(prev => 
        prev.map(conv => 
          conv.id === activeConversationId 
            ? { 
                ...conv, 
                aiId: newProviderId, 
                modelId: newModelId 
              }
            : conv
        )
      );
      
      setActiveProvider(newProviderId);
      setSelectedModels(prev => ({
        ...prev,
        [newProviderId]: newModelId
      }));
      
    } catch (error) {
      console.error('Erro ao trocar IA da conversa:', error);
      toast.error('Erro ao trocar IA da conversa');
    }
  }, [activeConversationId, availableProviders]);

  // Função para lidar com seleção de IA da tela default
  const handleSelectAI = useCallback(async (aiId: string, modelId: string) => {
    setActiveProvider(aiId);
    setSelectedModels(prev => ({
      ...prev,
      [aiId]: modelId
    }));

    // Criar nova conversa automaticamente
    try {
      const newConversation = await createNewConversation(aiId, modelId);
      setConversations(prev => [newConversation, ...prev]);
      setActiveConversationId(newConversation.id);
    } catch (error) {
      console.error('Erro ao criar conversa:', error);
    }
  }, [createNewConversation]);

  // Funções para agentes
  const handleAssistantSelect = useCallback(async (assistant: Assistant) => {
    console.log('🤖 Selecionando agente:', assistant.name);
    console.log('🔧 [UserDashboard] handleAssistantSelect tools:', {
      hasTools: !!assistant.tools,
      toolsCount: assistant.tools?.length || 0,
      tools: assistant.tools
    });
    setActiveAssistant(assistant);
    
    try {
      // Buscar TODAS as conversas existentes do agente
      const existingConversations = await assistantService.getAssistantConversations(assistant.id, user.id);
      console.log(`📊 Encontradas ${existingConversations.length} conversas para o agente`);
      
      if (existingConversations.length > 0) {
        // SEMPRE usar a conversa mais recente (primeira da lista, ordenada por updated_at desc)
        const latestConversation = existingConversations[0];
        console.log('📝 Carregando conversa existente:', latestConversation.id);
        
        // Carregar com mensagens
        const conversationWithMessages = await assistantService.getAssistantConversationWithMessages(latestConversation.id);
        
        // ✅ LIMPAR propriedade 'file' dos attachments de mensagens antigas para evitar erros de Blob inválido
        if (conversationWithMessages) {
          conversationWithMessages.messages = conversationWithMessages.messages.map(msg => ({
            ...msg,
            attachments: msg.attachments?.map(att => ({
              name: att.name,
              size: att.size,
              type: att.type,
              url: att.url
              // ❌ NÃO incluir 'file' - evita erro de processamento de Blob inválido
            }))
          }));
        }
        
        setAssistantConversation(conversationWithMessages);
        console.log(`✅ Conversa carregada com ${conversationWithMessages?.messages.length || 0} mensagens`);
      } else {
        // Só cria nova conversa se realmente não existir nenhuma
        console.log('🆕 Nenhuma conversa encontrada, criando nova...');
        const newConversation = await assistantService.createAssistantConversation(
          assistant.id,
          user.id,
          tenant.id,
          `Conversa com ${assistant.name}`
        );
        setAssistantConversation(newConversation);
        console.log('✅ Nova conversa criada:', newConversation.id);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar/criar conversa do agente:', error);
      toast.error('Erro ao iniciar conversa com agente');
    }
    
    if (isMobile) {
      // No mobile, vai direto para o chat do agente
      setMobileView('chat');
    } else {
      // No desktop, vai para a view de agente
      setCurrentView('assistant-chat');
    }
  }, [isMobile, user.id, tenant.id, toast]);

  const handleCreateAssistant = useCallback(() => {
    console.log('🎨 Criando agente do zero...');
    setSelectedTemplate(null);
    setEditingAssistant(null);
    setIsEditorModalOpen(true);
  }, []);

  const handleEditAssistant = useCallback((assistant: Assistant) => {
    setEditingAssistant(assistant);
    setIsEditorModalOpen(true);
  }, []);

  const handleAssistantSave = useCallback(async (assistant: Assistant) => {
    console.log('🔧 [UserDashboard] handleAssistantSave:', {
      assistantId: assistant.id,
      assistantName: assistant.name,
      hasTools: !!assistant.tools,
      toolsCount: assistant.tools?.length || 0,
      tools: assistant.tools
    });
    
    if (editingAssistant) {
      // Atualizando agente existente
      setAssistants(prev => prev.map(a => a.id === assistant.id ? assistant : a));
      
      // Se o agente editado é o ativo, atualiza ele também
      if (activeAssistant?.id === assistant.id) {
        setActiveAssistant(assistant);
      }
    } else {
      // Criando novo agente - adiciona à lista
      setAssistants(prev => [assistant, ...prev]);
    }
    
    setEditingAssistant(null);
    setIsEditorModalOpen(false);
  }, [editingAssistant, activeAssistant]);

  const handleAssistantDelete = useCallback((assistantId: string) => {
    // Remover agente da lista
    setAssistants(prev => prev.filter(a => a.id !== assistantId));
    
    // Se o agente excluído estava ativo, limpar
    if (activeAssistant?.id === assistantId) {
      setActiveAssistant(null);
      setAssistantConversation(null);
    }
  }, [activeAssistant]);

  const handleEditorModalClose = useCallback(() => {
    setEditingAssistant(null);
    setSelectedTemplate(null);
    setIsEditorModalOpen(false);
  }, []);

  const handleAssistantChatBack = useCallback(() => {
    setCurrentView('assistants');
    setActiveAssistant(null);
    setAssistantConversation(null);
  }, []);

  const handleAssistantConversationChange = useCallback((conversation: AssistantConversation) => {
    console.log('💬 Conversa do agente atualizada:', {
      id: conversation.id,
      messages: conversation.messages?.length || 0
    });
    setAssistantConversation(conversation);
  }, []);

  // Função para iniciar nova conversa com agente (limpa a atual, que já foi salva)
  const handleNewAssistantConversation = useCallback(() => {
    console.log('🆕 Iniciando nova conversa com agente');
    setAssistantConversation(null);
    toast.success('Nova conversa iniciada!');
  }, [toast]);

  // Função para excluir conversa
  const handleDeleteConversation = useCallback(async (conversationId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta conversa? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      await conversationService.deleteConversation(conversationId);
      
      // Remover conversa da lista local
      setConversations(prev => prev.filter(conv => conv.id !== conversationId));
      
      // Se a conversa excluída era a ativa, limpar seleção
      if (activeConversationId === conversationId) {
        setActiveConversationId(null);
        setActiveProvider(null);
      }
      
      toast.success('Conversa excluída com sucesso');
    } catch (error) {
      console.error('Erro ao excluir conversa:', error);
      toast.error('Erro ao excluir conversa');
    }
  }, [activeConversationId, toast]);

  const handleProfileClick = useCallback(() => {
    setShowUserProfile(true);
    if (isMobile) {
      setIsMobileSidebarOpen(false);
    }
  }, [isMobile]);

  const handleOnboardingComplete = useCallback(async (onboardingData: any) => {
    try {
      console.log('📝 Salvando dados do onboarding...');
      
      // Atualizar metadados do usuário no Supabase Auth
      await supabase.auth.updateUser({
        data: {
          onboarding_completed: true,
          account_type: onboardingData.accountType,
          company_name: onboardingData.companyName,
          cnpj: onboardingData.cnpj,
          segment: onboardingData.segment,
          team_size: onboardingData.teamSize,
          ai_knowledge: onboardingData.aiKnowledge
        }
      });

      // Se for empresa, atualizar o nome do tenant
      if (onboardingData.accountType === 'business' && onboardingData.companyName) {
        await tenantService.updateTenantSettings(tenant.id, {
          ...tenant.settings,
          companyName: onboardingData.companyName
        });
      }

      setShowOnboarding(false);
      setOnboardingCompleted(true);
      toast.success('Perfil configurado com sucesso! Bem-vindo ao Space AI 🚀');
    } catch (error) {
      console.error('Erro ao salvar onboarding:', error);
      toast.error('Erro ao salvar suas informações. Tente novamente.');
    }
  }, [tenant.id, tenant.settings, toast]);

  const handleOnboardingSkip = useCallback(async () => {
    try {
      // Marcar como completado mesmo pulando
      await supabase.auth.updateUser({
        data: {
          onboarding_completed: true
        }
      });

      setShowOnboarding(false);
      setOnboardingCompleted(true);
      toast.success('Você pode completar seu perfil a qualquer momento!');
    } catch (error) {
      console.error('Erro ao pular onboarding:', error);
      setShowOnboarding(false);
      setOnboardingCompleted(true);
    }
  }, [toast]);

  const handleUserManagement = useCallback(() => {
    setCurrentView('user-management');
    if (isMobile) {
      setIsMobileSidebarOpen(false);
    }
  }, [isMobile]);

  const handleBackFromUserManagement = useCallback(() => {
    setCurrentView('chat');
  }, []);

  const handleSendMessage = useCallback(async (content: string, enabledTools?: string[], forcedTools?: string[], files?: File[], aiId?: string, modelId?: string) => {
    const currentAI = aiId || activeProvider;
    const currentModel = modelId || (activeProvider ? selectedModels[activeProvider] : null);
    
    if (!currentAI || !currentModel) {
      toast.error('Selecione uma IA primeiro');
      return;
    }

    // ✅ VERIFICAR LIMITE DE MENSAGENS ANTES DE ENVIAR
    const canSend = await plansService.canSendMessage(tenant.id, user.id);
    if (!canSend) {
      console.log('❌ Limite de mensagens atingido! Abrindo modal de upsell...');
      setShowUpsellModal(true);
      toast.error('Você atingiu o limite de mensagens diárias do plano gratuito');
      return;
    }

    // Se chamado da tela default, configurar o estado primeiro
    if (aiId && modelId) {
      setActiveProvider(aiId);
      setSelectedModels(prev => ({
        ...prev,
        [aiId]: modelId
      }));
    }

    // Usar conversa ativa ou criar nova
    let currentConversation = activeConversationId ? 
      conversations.find(c => c.id === activeConversationId) : null;

    if (!currentConversation) {
      try {
        currentConversation = await createNewConversation(currentAI, currentModel);
        setConversations(prev => [currentConversation!, ...prev]);
        setActiveConversationId(currentConversation.id);
      } catch (error) {
        console.error('Erro ao criar conversa:', error);
        return;
      }
    }

    // Converter arquivos para formato de attachment
    const attachments = files?.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type,
      file // Manter File object para preview local
    }));

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      content,
      role: 'user',
      timestamp: new Date(),
      attachments
    };

    // Adicionar mensagem do usuário à conversa
    setConversations(prev => {
      return prev.map(conv => 
        conv.id === currentConversation!.id 
          ? { ...conv, messages: [...conv.messages, userMessage] }
          : conv
      );
    });

    // Salvar mensagem no Supabase
    try {
      await conversationService.addMessage(currentConversation.id, content, 'user', undefined, undefined, attachments);
    } catch (error) {
      console.error('❌ Erro ao salvar mensagem do usuário:', error);
    }

    setIsLoading(true);
    
    // Mostrar feedback visual das ferramentas forçadas em execução
    if (forcedTools && forcedTools.length > 0) {
      const toolLabels: Record<string, string> = {
        'web_search': 'Busca na Web',
        'google_calendar': 'Google Calendar',
        'google_drive': 'Google Drive'
      };
      
      setExecutingTools(forcedTools.map(id => ({
        id,
        name: toolLabels[id] || id,
        status: 'executing' as const
      })));
    }

    try {
      // Processar arquivos (Excel/CSV/PDF) e adicionar ao conteúdo da mensagem
      let enrichedContent = content;
      
      if (files && files.length > 0) {
        console.log('📎 Processando arquivos anexados...');
        
        // Processar planilhas (Excel/CSV)
        const spreadsheetFiles = files.filter(isSpreadsheet);
        if (spreadsheetFiles.length > 0) {
          console.log(`📊 Encontrados ${spreadsheetFiles.length} arquivo(s) Excel/CSV`);
          
          for (const file of spreadsheetFiles) {
            try {
              const spreadsheetData = await processSpreadsheet(file);
              enrichedContent += `\n\n${spreadsheetData}`;
              console.log(`✅ Planilha ${file.name} processada com sucesso`);
            } catch (error) {
              console.error(`❌ Erro ao processar ${file.name}:`, error);
              toast.error(`Erro ao processar ${file.name}`);
            }
          }
        }
        
        // Processar PDFs
        const pdfFiles = files.filter(isPDF);
        if (pdfFiles.length > 0) {
          console.log(`📄 Encontrados ${pdfFiles.length} arquivo(s) PDF`);
          
          for (const file of pdfFiles) {
            try {
              console.log(`📄 Processando PDF: ${file.name}...`);
              toast.info(`Processando PDF: ${file.name}...`);
              const pdfData = await processPDF(file);
              enrichedContent += `\n\n${pdfData}`;
              console.log(`✅ PDF ${file.name} processado com sucesso`);
              toast.success(`PDF ${file.name} processado!`);
            } catch (error) {
              console.error(`❌ Erro ao processar PDF ${file.name}:`, error);
              toast.error(`Erro ao processar PDF ${file.name}`);
            }
          }
        }
      }
      
      // Criar mensagem enriquecida com dados das planilhas
      const enrichedUserMessage = enrichedContent !== content 
        ? { ...userMessage, content: enrichedContent }
        : userMessage;

      const messagesForAPI = [...currentConversation.messages, enrichedUserMessage];
      
      // Usar o serviço de IA com restrições de conteúdo e opções de função
      // As restrições são aplicadas automaticamente no prompt do sistema do GPT
      // incluindo temas proibidos, nível de moderação e instruções personalizadas
      const contentRestrictions = tenant.settings?.contentRestrictions;
      const aiResult = await aiService.sendMessage(currentAI, messagesForAPI, currentModel, contentRestrictions, {
        tenantId: tenant.id,
        userId: user.id,
        enabledTools: enabledTools || [],
        forcedTools: forcedTools || []
      });

      console.log('🔍 aiResult recebido no UserDashboard:', {
        hasFiles: !!aiResult.files,
        filesCount: aiResult.files?.length || 0,
        files: aiResult.files,
        hasToolCalls: !!aiResult.toolCalls,
        toolCallsCount: aiResult.toolCalls?.length || 0,
        toolCalls: aiResult.toolCalls
      });

      const provider = availableProviders.find(p => p.id === currentAI);
      const model = provider?.models.find(m => m.id === currentModel);

      const aiMessage: Message = {
        id: `ai-${Date.now()}`,
        content: aiResult.content,
        role: 'assistant',
        timestamp: new Date(),
        modelId: currentModel,
        modelName: model?.name,
        generatedFiles: aiResult.files, // ✨ Incluir arquivos gerados
        toolCalls: aiResult.toolCalls // ✨ Incluir tool calls executadas
      };

      console.log('💬 aiMessage criada:', {
        hasGeneratedFiles: !!aiMessage.generatedFiles,
        filesCount: aiMessage.generatedFiles?.length || 0,
        generatedFiles: aiMessage.generatedFiles,
        hasToolCalls: !!aiMessage.toolCalls,
        toolCallsCount: aiMessage.toolCalls?.length || 0,
        toolCalls: aiMessage.toolCalls
      });

      // Adicionar resposta da IA à conversa
      setConversations(prev => {
        return prev.map(conv => 
          conv.id === currentConversation!.id 
            ? { ...conv, messages: [...conv.messages, aiMessage] }
            : conv
        );
      });

      // Salvar resposta da IA no Supabase com tokens
      try {
        await conversationService.addMessage(
          currentConversation.id, 
          aiResult.content, 
          'assistant', 
          currentModel, 
          model?.name,
          undefined, // attachments
          aiResult.tokens,
          currentAI
        );
        
        await plansService.incrementUsage(tenant.id, user.id, 'all');
        setRefreshTrigger(prev => prev + 1);
        
      } catch (error) {
        console.error('Erro ao salvar resposta da IA:', error);
      }

      // Atualizar título da conversa se for a primeira mensagem
      if (currentConversation.messages.length === 0) {
        const newTitle = content.length > 50 ? content.substring(0, 50) + '...' : content;
        try {
          await conversationService.updateConversationTitle(currentConversation.id, newTitle);
          setConversations(prev => 
            prev.map(conv => 
              conv.id === currentConversation!.id 
                ? { ...conv, title: newTitle }
                : conv
            )
          );
        } catch (error) {
          console.error('Erro ao atualizar título da conversa:', error);
        }
      }

      // Resposta recebida com sucesso (sem toast para não poluir a UI)

    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      
      // Adicionar mensagem de erro
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        content: `Desculpe, ocorreu um erro ao processar sua mensagem: ${error instanceof Error ? error.message : 'Erro desconhecido'}. Tente novamente.`,
        role: 'assistant',
        timestamp: new Date(),
        modelId: currentModel,
        modelName: `${availableProviders.find(p => p.id === currentAI)?.models.find(m => m.id === currentModel)?.name} (Erro)`
      };

      setConversations(prev => {
        return prev.map(conv => 
          conv.id === currentConversation!.id 
            ? { ...conv, messages: [...conv.messages, errorMessage] }
            : conv
        );
      });

      // Se houve erro, limpar conversas vazias que podem ter sido criadas
      try {
        await conversationService.cleanupEmptyConversations(user.id, tenant.id);
      } catch (cleanupError) {
        console.warn('Aviso: Não foi possível limpar conversas vazias após erro:', cleanupError);
      }
    } finally {
      setIsLoading(false);
      // Limpar feedback de ferramentas
      setExecutingTools([]);
    }
  }, [activeProvider, selectedModels, createNewConversation, availableProviders, conversations, activeConversationId, user.id, tenant.id, toast]);

  const currentProvider = activeProvider ? availableProviders.find(p => p.id === activeProvider) : null;
  
  // Buscar a conversa ativa
  const currentConversation = activeConversationId ? 
    conversations.find(conv => conv.id === activeConversationId) || null : null;

  // Renderização mobile
  if (isMobile) {
    return (
      <>
        {mobileView === 'home' ? (
          <MobileInitialScreen
            availableProviders={availableProviders}
            assistants={assistants}
            onProviderSelect={handleMobileProviderSelect}
            onAssistantSelect={handleAssistantSelect}
            onCreateAssistant={handleCreateAssistant}
            onMenuClick={() => setIsMobileSidebarOpen(true)}
          />
        ) : activeAssistant ? (
          // Chat com agente no mobile
          <AssistantChatWrapper
            assistant={activeAssistant}
            user={user}
            tenant={tenant}
            conversation={assistantConversation}
            onConversationChange={handleAssistantConversationChange}
            onUsageUpdate={() => setRefreshTrigger(prev => prev + 1)}
            onShowUpsell={() => setShowUpsellModal(true)}
            onEditAssistant={() => handleEditAssistant(activeAssistant)}
            onBackClick={handleMobileBackToHome}
            onNewConversation={handleNewAssistantConversation}
          />
        ) : currentProvider ? (
          // Chat com provedor de IA no mobile
          <MobileChatLayout
            user={user}
            tenant={tenant}
            provider={currentProvider}
            modelId={activeProvider ? selectedModels[activeProvider] || '' : ''}
            conversation={currentConversation}
            messages={currentConversation?.messages || []}
            isLoading={isLoading}
            onSendMessage={(content: string) => {
              if (activeProvider) {
                const modelId = selectedModels[activeProvider] || '';
                handleSendMessage(content, undefined, undefined, undefined, activeProvider, modelId);
              }
            }}
            onBackToHome={handleMobileBackToHome}
            onMenuClick={() => setIsMobileSidebarOpen(true)}
          />
        ) : null}
        
        <MobileSidebarDrawer
          isOpen={isMobileSidebarOpen}
          onClose={() => setIsMobileSidebarOpen(false)}
          user={user}
          tenant={tenant}
          providers={availableProviders}
          activeProvider={activeProvider}
          onProviderSelect={handleMobileProviderSelect}
          assistants={assistants}
          onAssistantSelect={handleAssistantSelect}
          onCreateAssistant={handleCreateAssistant}
          conversations={conversations}
          onConversationSelect={handleMobileConversationSelect}
          onDeleteConversation={handleDeleteConversation}
          onNewChat={handleMobileNewChat}
          onProfileClick={handleProfileClick}
        />
        
        {/* Modal de Upsell quando atingir o limite - Mobile */}
        <PlansModal
          isOpen={showUpsellModal}
          onClose={() => setShowUpsellModal(false)}
          tenantId={tenant.id}
          currentPlan="free"
        />

        {/* Modal de Perfil - Mobile */}
        {showUserProfile && (
          <UserProfile
            user={user}
            tenant={tenant}
            onClose={() => setShowUserProfile(false)}
            onLogout={onLogout}
            onUpdate={(updatedUser) => {
              if (onUserUpdate) onUserUpdate(updatedUser);
              setShowUserProfile(false);
            }}
          />
        )}

        {/* Modal de Editor de Agente - Mobile */}
        <AssistantEditorModal
          isOpen={isEditorModalOpen}
          user={user}
          tenant={tenant}
          assistant={editingAssistant || undefined}
          template={selectedTemplate}
          onSave={handleAssistantSave}
          onClose={handleEditorModalClose}
          onDelete={handleAssistantDelete}
        />

      </>
    );
  }

  // Renderização desktop (original)
  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      <div className="flex-shrink-0">
        <TopHeader 
          user={user} 
          tenant={tenant}
          onLogout={onLogout} 
          onCompanySettings={(user.role === 'owner' || user.role === 'manager') ? onCompanySettings : undefined}
          onUserManagement={(user.role === 'owner' || user.role === 'manager') ? handleUserManagement : undefined}
          onProfileClick={handleProfileClick}
          refreshTrigger={refreshTrigger}
          onMenuToggle={() => setIsDesktopSidebarOpen(!isDesktopSidebarOpen)}
          showMenuButton={true}
        />
      </div>
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Overlay para fechar sidebar em telas médias */}
        {isDesktopSidebarOpen && (
          <div 
            className="fixed inset-0 top-[41px] bg-black/30 z-[60] lg:hidden"
            onClick={() => setIsDesktopSidebarOpen(false)}
          />
        )}
        
        {/* Sidebar - responsiva */}
        <div className={`
          ${isDesktopSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          fixed top-[41px] left-0 h-[calc(100vh-41px)] lg:relative lg:top-0 lg:h-full lg:translate-x-0
          z-[70] lg:z-auto
          transition-transform duration-300 ease-in-out
          shadow-xl lg:shadow-none
        `}>
          <NewAISidebar
            providers={availableProviders}
            activeProvider={activeProvider}
            onProviderSelect={(providerId) => {
              handleProviderSelect(providerId);
              // Fechar sidebar em telas menores após seleção
              if (window.innerWidth < 1024) {
                setIsDesktopSidebarOpen(false);
              }
            }}
            assistants={assistants}
            onAssistantSelect={(assistant) => {
              handleAssistantSelect(assistant);
              // Fechar sidebar em telas menores após seleção
              if (window.innerWidth < 1024) {
                setIsDesktopSidebarOpen(false);
              }
            }}
            onCreateAssistant={() => {
              handleCreateAssistant();
              if (window.innerWidth < 1024) {
                setIsDesktopSidebarOpen(false);
              }
            }}
            conversations={conversations}
            activeConversationId={activeConversationId || undefined}
            onConversationSelect={(conversationId) => {
              handleConversationSelect(conversationId);
              if (window.innerWidth < 1024) {
                setIsDesktopSidebarOpen(false);
              }
            }}
            onNewConversation={handleNewConversation}
            onDeleteConversation={handleDeleteConversation}
          />
        </div>
        
        <div className="flex-1 flex flex-col">
          {currentView === 'assistants' ? (
            <AssistantsList
              user={user}
              tenant={tenant}
              onAssistantSelect={handleAssistantSelect}
              onCreateAssistant={handleCreateAssistant}
              onEditAssistant={handleEditAssistant}
            />
          ) : currentView === 'user-management' ? (
            <UserManagementScreen
              user={user}
              tenant={tenant}
              onBack={handleBackFromUserManagement}
            />
          ) : currentView === 'assistant-chat' && activeAssistant ? (
            <AssistantChatWrapper
              assistant={activeAssistant}
              user={user}
              tenant={tenant}
              conversation={assistantConversation}
              onConversationChange={handleAssistantConversationChange}
              onUsageUpdate={() => setRefreshTrigger(prev => prev + 1)}
              onShowUpsell={() => setShowUpsellModal(true)}
              onEditAssistant={() => handleEditAssistant(activeAssistant)}
              onBackClick={handleAssistantChatBack}
              onNewConversation={handleNewAssistantConversation}
            />
          ) : currentProvider ? (
            <NewChatInterface
              provider={currentProvider}
              availableProviders={availableProviders}
              conversation={currentConversation}
              selectedModel={activeProvider ? selectedModels[activeProvider] || '' : ''}
              onModelChange={handleModelChange}
              onProviderChange={handleProviderChange}
              onSendMessage={handleSendMessage}
              isLoading={isLoading}
              userId={user.id}
              externalExecutingTools={executingTools}
            />
          ) : (
            <DefaultChatScreen
              tenant={tenant}
              assistants={assistants}
              onSelectAI={handleSelectAI}
              onAssistantSelect={handleAssistantSelect}
              onSendMessage={(message: string, aiId: string, modelId: string) => 
                handleSendMessage(message, undefined, undefined, undefined, aiId, modelId)
              }
            />
          )}
        </div>
      </div>
      
      {/* Modal de Upsell quando atingir o limite */}
      <PlansModal
        isOpen={showUpsellModal}
        onClose={() => setShowUpsellModal(false)}
        tenantId={tenant.id}
        currentPlan="free"
      />

      {/* Modal de Perfil - Desktop */}
      {showUserProfile && (
        <UserProfile
          user={user}
          tenant={tenant}
          onClose={() => setShowUserProfile(false)}
          onLogout={onLogout}
          onUpdate={(updatedUser) => {
            if (onUserUpdate) onUserUpdate(updatedUser);
            setShowUserProfile(false);
          }}
        />
      )}

      {/* Modal de Onboarding */}
      {showOnboarding && (
        <OnboardingModal
          userName={user.name}
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}

      {/* Modal de Editor de Agente - Desktop */}
      <AssistantEditorModal
        isOpen={isEditorModalOpen}
        user={user}
        tenant={tenant}
        assistant={editingAssistant || undefined}
        template={selectedTemplate}
        onSave={handleAssistantSave}
        onClose={handleEditorModalClose}
        onDelete={handleAssistantDelete}
      />

    </div>
  );
}